import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FirstLoanOfferResponse } from 'api/typegen/application/first-loan-offer/get';
import { useDebounce } from 'usehooks-ts';
import useAmountConstraints from '../LoanApplicationProcessLayout/LoanCalculatorSettings/useAmountConstraints';
import useLoanApplicationProcess from '../LoanApplicationProcessLayout/LoanApplicationProcessProvider/useLoanApplicationProcess';
import useLoanOffer from '../LoanApplicationProcessLayout/Sidebar/useLoanOffer';

type CalculatorContextType = {
  shouldDisplayLoading: boolean;
  amount: number | undefined;
  onAmountChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  offer?: FirstLoanOfferResponse;
};

const CalculatorContext = createContext<CalculatorContextType>(
  {} as CalculatorContextType,
);

export function useCalculator() {
  return useContext(CalculatorContext);
}

function useCalculatorValue() {
  const amountConstraintsQuery = useAmountConstraints();
  const { loanAmount, setLoanAmount } = useLoanApplicationProcess();

  const { amountConstraints } = amountConstraintsQuery;

  const loanOfferQuery = useLoanOffer();

  const shouldDisplayLoading = amountConstraintsQuery.status === 'loading';

  const [amount, setAmount] = useState<number | undefined>();
  const debouncedAmount = useDebounce(amount);

  const min = amountConstraints?.min;
  const max = amountConstraints?.max;
  const step = amountConstraints?.step;

  useEffect(() => {
    if (
      amountConstraintsQuery.status !== 'loading' &&
      amountConstraints &&
      amountConstraints.defaultValue !== undefined &&
      max &&
      min
    ) {
      const validLoanAmount = loanAmount <= max && loanAmount >= min;
      const nextAmount = validLoanAmount
        ? loanAmount
        : amountConstraints.defaultValue;
      setAmount(nextAmount);
    }
  }, [
    amountConstraintsQuery.status,
    max,
    min,
    amountConstraints?.defaultValue,
  ]);

  const onAmountChange = useCallback(
    (value: number) => {
      setAmount(value);
      // handleAnalytics()
    },
    [setAmount],
  );

  useEffect(() => {
    if (debouncedAmount !== undefined) {
      setLoanAmount(debouncedAmount);
    }
  }, [debouncedAmount]);

  return {
    shouldDisplayLoading,
    amount,
    onAmountChange,
    min,
    max,
    step,
    offer: loanOfferQuery.loanOffer,
  };
}

function CalculatorProvider({ children }: PropsWithChildren) {
  const value = useCalculatorValue();

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
}

export default CalculatorProvider;
